type Handler = (message: Record<string, unknown>) => void;

const HEARTBEAT_INTERVAL_MS = 25_000;
const HEARTBEAT_TIMEOUT_MS = 60_000;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
const UNAUTHORIZED_CLOSE_CODE = 1008;
const CLIENT_ID_KEY = 'calls:client-id';

function getVertexWsBase(): string {
  const explicit = import.meta.env.VITE_VERTEX_WS_URL;
  if (explicit?.trim()) return explicit.trim().replace(/\/$/, '');
  const http = import.meta.env.VITE_VERTEX_BASE_URL || 'http://localhost:3050';
  return http.replace(/^http/i, 'ws').replace(/\/$/, '');
}

function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function getClientId(): string {
  try {
    const existing = sessionStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const id = newClientId();
    sessionStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return newClientId();
  }
}

export function connectCallsWs(onMessage: Handler): { close: () => void } {
  const base = getVertexWsBase();
  let socket: WebSocket | null = null;
  let stopped = false;
  let attempt = 0;
  let lastSeen = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

  const clearHeartbeat = () => {
    if (heartbeatTimer) clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  };

  const startHeartbeat = (ws: WebSocket) => {
    clearHeartbeat();
    lastSeen = Date.now();
    heartbeatTimer = setInterval(() => {
      if (Date.now() - lastSeen > HEARTBEAT_TIMEOUT_MS) {
        dropSocket(ws);
        return;
      }
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  };

  const scheduleReconnect = () => {
    if (stopped || reconnectTimer) return;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
    attempt += 1;
    const jittered = delay * (0.5 + Math.random() / 2);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, jittered);
  };

  function dropSocket(ws: WebSocket) {
    if (ws !== socket) return;
    socket = null;
    clearHeartbeat();
    try {
      ws.close();
    } catch {
      /* already closed */
    }
    scheduleReconnect();
  }

  function connect() {
    const token = localStorage.getItem('token') || '';
    if (!token) {
      scheduleReconnect();
      return;
    }
    const ws = new WebSocket(`${base}/calls`);
    socket = ws;

    ws.onopen = () => {
      if (ws !== socket) return;
      ws.send(JSON.stringify({ type: 'auth', token, clientId: getClientId() }));
      startHeartbeat(ws);
    };

    ws.onmessage = (ev) => {
      if (ws !== socket) return;
      lastSeen = Date.now();
      let data: Record<string, unknown>;
      try {
        data = JSON.parse(String(ev.data)) as Record<string, unknown>;
      } catch {
        return;
      }
      if (data.type === 'pong' || data.type === 'auth.ok') {
        attempt = 0;
        if (data.type === 'auth.ok') return;
        return;
      }
      if (data.type === 'auth.error') {
        stopped = true;
      }
      onMessage(data);
    };

    ws.onclose = (ev) => {
      if (ws !== socket) return;
      if (ev.code === UNAUTHORIZED_CLOSE_CODE) stopped = true;
      dropSocket(ws);
    };
  }

  connect();

  return {
    close: () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      clearHeartbeat();
      const ws = socket;
      socket = null;
      ws?.close();
    },
  };
}
